/**
 * PMS Realtime Module
 *
 * Wraps Supabase Realtime channels so the raw client
 * is never imported outside `src/integrations/pms/`.
 */

import { supabase } from '@/integrations/supabase/client';
import type { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';

export type { RealtimeChannel, RealtimePostgresChangesPayload };

export type RealtimeEvent = 'INSERT' | 'UPDATE' | 'DELETE' | '*';

export interface SubscribeOptions {
  /** Channel name — must be unique per subscription. */
  channelName: string;
  /** Postgres table to listen on. */
  table: string;
  /** Schema (defaults to 'public'). */
  schema?: string;
  /** Filter to a specific event type (defaults to '*'). */
  event?: RealtimeEvent;
  /** Optional filter string e.g. 'user_id=eq.abc'. */
  filter?: string;
  /** Callback for every change. */
  onPayload: (payload: RealtimePostgresChangesPayload<any>) => void;
}

/**
 * Subscribe to Postgres changes on a table.
 * Returns an unsubscribe function.
 */
export function subscribeToTable(options: SubscribeOptions): () => void {
  const {
    channelName,
    table,
    schema = 'public',
    event = '*',
    filter,
    onPayload,
  } = options;

  const channelConfig: any = {
    event,
    schema,
    table,
  };
  if (filter) channelConfig.filter = filter;

  const channel = supabase
    .channel(channelName)
    .on('postgres_changes', channelConfig, onPayload)
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

/**
 * Create a raw realtime channel (escape hatch for complex subscriptions).
 */
export function pmsChannel(name: string) {
  return supabase.channel(name);
}

/**
 * Remove a realtime channel.
 */
export function pmsRemoveChannel(channel: RealtimeChannel) {
  return supabase.removeChannel(channel);
}
